
@if (templateMessage$ | async; as templateMessage) {
  @if (templateMessage) {
    <div
      class="template-message"
      [class.critical]="templateMessageBackgroundCritical"
      >
      <p class="body">{{ templateMessage }}</p>
    </div>
  }
}
@let _formChanges = previewForm.valueChanges | async;
<form class="edit-template-form"
    [formGroup]="previewForm"
    [class.substract-height]="templateMessage$ | async"
    (ngSubmit)="onSubmit()"
    >
    <div class="top">
      <div class="template-title">
        <div class="header-title">
          <label class="auto-growing-input">
            <input
              formControlName="name"
              autocomplete="off"
              onInput="this.parentNode.dataset.value = this.value"
              (click)="enableNameEdit()"
              (blur)="onBlurEditName()"
              (keydown.enter)="disableNameEdit()"
              (keydown.escape)="disableNameEdit()"
              [placeholder]="'Name this template' | translate"
              #previewFormName>
            </label>
            <i class="fa-solid fa-pen-to-square suffix" (click)="enableNameEdit()"></i>
          </div>
          <p class="caption">
            {{ 'TEMPLATE_LATEST_MODIFIED_BY' | translate : {
            modifiedDate: (
            previewForm.controls.modifiedDate.value
            ? (previewForm.controls.modifiedDate.value | date: 'yyyy-MM-dd')
            : ''
            ),
            modifiedBy: previewForm.controls.modifiedBy.value
          }
          }}
        </p>
      </div>
      <app-validation-message [control]="previewForm.controls.name"></app-validation-message>
      @if (previewForm.controls.automaticDescription.value) {
        <div class="description">
          <p>{{ previewForm.controls.automaticDescription.value }}</p>
        </div>
      }
    </div>
    @if ({ selectedTextarea: selectedTextareaToRecieveChip$ | async }; as obs) {
      <div
        class="content"
        >
        <div class="preview">
          <div class="template" >
            <app-textarea-with-chips
              [class.selected-text-area]="obs.selectedTextarea === 'summary'"
                    [options]="{
                        control: previewForm.controls.summary,
                        id: 'summary',
                        label: 'Summarized',
                        caption: 'A short message shown in journey planner search results, on board the vehicle, and at stops.',
                        editorMode: true
                    }"
              [editable]="'plaintext-only'"
              (focused)="onFocusSelectedTextarea($event)"
              #textarea>
            </app-textarea-with-chips>
            <app-validation-message [control]="previewForm.controls.summary">
            </app-validation-message>
            <app-textarea-with-chips
              [class.selected-text-area]="obs.selectedTextarea === 'header'"
                    [options]="{
                        control: previewForm.controls.header,
                        id: 'header',
                        label: 'Detailed',
                        caption: 'A message shown in the traffic view in the app and on the web. Both a heading and a message are required.',
                        asTextInput: true,
                        editorMode: true
                    }"
              [editable]="'plaintext-only'"
              style="margin-bottom: 6px"
              (focused)="onFocusSelectedTextarea($event)"
              #textarea>
            </app-textarea-with-chips>
            <app-validation-message [control]="previewForm.controls.header">
            </app-validation-message>
            <app-textarea-with-chips
              [class.selected-text-area]="obs.selectedTextarea === 'content'"
                    [options]="{
                        control: previewForm.controls.content,
                        id: 'content',
                        editorMode: true
                    }"
              [editable]="'plaintext-only'"
              (focused)="onFocusSelectedTextarea($event)"
              #textarea>
            </app-textarea-with-chips>
            <app-validation-message [control]="previewForm.controls.content">
            </app-validation-message>
            <app-information-details
              [requiredCustomControls$]="requiredCustomControls$"
              [editView]="true">
            </app-information-details>
          </div>
        </div>
        <div class="choices">
          <div class="template-variables-library">
            <div class="subheader">
              <p class="subtitle" translate>Variables</p>
              <p class="caption" translate>Click on a variable below to add the variable to the selected template where your cursor is</p>
            </div>
            <div class="automatic-handling">
              <div class="subheader">
                <p class="base" translate>Automatic handling</p>
                <p class="caption" translate>Retrieves information previously entered in the deviation</p>
              </div>
              <div class="chips-wrapper">
                @for (automaticVariable of automaticVariables$ | async; track automaticVariable) {
                  <div
                    class="chip"
                    (click)="addChipToSelectedTextarea(automaticVariable)"
                    [title]="!obs.selectedTextarea ? ('CHIP_DISABLED_HOVER_TITLE' | translate) : ''">
                    {{ automaticVariable | translate }}
                  </div>
                }
              </div>
            </div>
            <div class="manual-handling">
              <div class="subheader">
                <p class="base" translate>Manual handling</p>
                <p class="caption" translate>Generates a field that needs to be manually filled in by the traffic controller</p>
              </div>
              <div class="chips-wrapper">
                @for (manualVariable of manuelVariables$ | async; track manualVariable) {
                  <div
                    class="chip"
                    (click)="addChipToSelectedTextarea(manualVariable)"
                    [title]="!obs.selectedTextarea ? ('CHIP_DISABLED_HOVER_TITLE' | translate) : ''">
                    {{ manualVariable | translate }}
                  </div>
                }
                @if (previewForm.controls.addingChip.enabled) {
                  <input type="text"
                    class="chip adding-chip"
                    formControlName="addingChip"
                    (keydown.escape)="previewForm.controls.addingChip.disable()"
                    (keydown.enter)="onAddingChip(addingChip.value)"
                    autocomplete="off"
                    autofocus
                    #addingChip>
                }
                <div class="add-variable"
                  (click)="toggleAddingChip()"
                            [title]="!obs.selectedTextarea ?
                                ('CHIP_DISABLED_HOVER_TITLE' | translate) : ''">
                  <i class="fa-solid fa-plus"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="template-deviation-type">
            <div class="subheader">
              <p class="subtitle" translate>Deviation type</p>
              <p class="caption" translate>The template is used for the following deviation types</p>
            </div>
            <div class="checkboxes-wrapper" formGroupName="deviationTypes">
              @for (checkbox of previewForm.controls.deviationTypes.controls | keyvalue; track checkbox.key) {
                <div class="checkbox-group"
                  >
                  <div class="custom-checkbox">
                    <input type="checkbox"
                      [formControlName]="checkbox.key"
                      [id]="checkbox.key"
                      [checked]="checkbox.value" />
                      <span class="focus-background"></span>
                    </div>
                    <label [for]="checkbox.key">{{ checkbox.key | translate }}</label>
                  </div>
                }
              </div>
            </div>
            <div class="form-buttons">
              <button
                type="button"
                (click)="onSubmit()"
                [disabled]="!previewForm.valid || (isDirty$ | async) !== true">
                {{ 'Publish' | translate }}
              </button>
              @if (!previewForm.controls.automaticTemplate.value) {
                <button
                  type="button"
                  class="destructive"
                  (click)="deleteTemplate()"
                  >
                  {{ 'Delete' | translate }}
                </button>
              }
            </div>
          </div>
        </div>
      }
    </form>